/**
 * Dates of the operation / exercise (R2-09; 2026-10-22, Dr. Tatar).
 *
 * ONE source: operation_parameters.operation_start_date ('YYYY-MM-DD') and operation_start_time_z ('HH:MM', Zulu = UTC). Tab 2 and
 * Tab 7 show and edit the same two fields; they are set once and locked when the exercise clock has been started. The end date is
 * derived from the length of the operation (duration_days), it is not typed. Day 1 is the start date; day N is start + N - 1 days at
 * the same Zulu hour. Everything here is UTC, so a daylight-saving change of the computer cannot move an hour.
 *
 * Season of a date: the meteorological seasons (spring March to May, summer June to August, fall September to November, winter December
 * to February in the Northern Hemisphere; NOAA / WMO convention, SOURCED). Southern Hemisphere: the same months six months apart (summer
 * December to February, winter June to August). The hemisphere is a Tab 2 setting (climate_config.hemisphere, default NORTH). The seasonal
 * rates of the tool are tuned for temperate Europe: in the south only the season NAMES follow the calendar. Jungle / tropical uses dry and
 * wet seasons that a date cannot give: for that region the planner keeps the season selector.
 */

export const DATE_SEASON_ON = true;
export const DEFAULT_START_TIME_Z = '06:00';

const MS_DAY = 86_400_000;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const pad = (n) => String(n).padStart(2, '0');

/** Date object (UTC) of the start of the operation, or null when no date is set. Throws on a malformed date or time. */
export function startInstant(startDate, startTimeZ = DEFAULT_START_TIME_Z) {
  if (!startDate) return null;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(startDate)) throw new Error(`exerciseDates: start date must be YYYY-MM-DD (got: ${startDate})`);
  const time = startTimeZ || DEFAULT_START_TIME_Z;
  if (!/^\d{2}:\d{2}$/.test(time)) throw new Error(`exerciseDates: start time must be HH:MM (got: ${time})`);
  const d = new Date(`${startDate}T${time}:00Z`);
  if (Number.isNaN(d.getTime())) throw new Error(`exerciseDates: not a valid date and time: ${startDate} ${time}`);
  return d;
}

/** Instant (UTC) of operation day N (day 1 = the start). */
export function instantOfDay(startDate, startTimeZ, day) {
  const s = startInstant(startDate, startTimeZ);
  if (!s) return null;
  if (typeof day !== 'number' || Number.isNaN(day) || day < 1) throw new Error(`exerciseDates: day must be a number >= 1 (got: ${day})`);
  return new Date(s.getTime() + (day - 1) * MS_DAY);
}

/** Last day of the operation: the start date plus duration_days - 1. */
export function endInstant(startDate, startTimeZ, durationDays) {
  if (typeof durationDays !== 'number' || Number.isNaN(durationDays) || durationDays < 1) throw new Error(`exerciseDates: duration must be a number >= 1 (got: ${durationDays})`);
  return instantOfDay(startDate, startTimeZ, durationDays);
}

export const HEMISPHERES = Object.freeze(['NORTH', 'SOUTH']);

/** Meteorological season of a date: 'spring' | 'summer' | 'fall' | 'winter'. hemisphere 'NORTH' (default) or 'SOUTH' (six months apart). */
export function seasonOfInstant(instant, hemisphere = 'NORTH') {
  if (!HEMISPHERES.includes(hemisphere)) throw new Error(`exerciseDates: hemisphere must be NORTH or SOUTH (got: ${hemisphere})`);
  const m = hemisphere === 'SOUTH' ? (instant.getUTCMonth() + 6) % 12 : instant.getUTCMonth();   // 0 = January
  if (m >= 2 && m <= 4) return 'spring';
  if (m >= 5 && m <= 7) return 'summer';
  if (m >= 8 && m <= 10) return 'fall';
  return 'winter';
}

/** Season of operation day N, or null when no date is set (the planner's selector then applies). */
export function seasonOfDay(startDate, startTimeZ, day, hemisphere = 'NORTH') {
  const i = instantOfDay(startDate, startTimeZ, day);
  return i ? seasonOfInstant(i, hemisphere) : null;
}

/** '22 Oct 2026 06:00Z' */
export function formatZulu(instant) {
  if (!instant) return '';
  return `${pad(instant.getUTCDate())} ${MONTHS[instant.getUTCMonth()]} ${instant.getUTCFullYear()} ${pad(instant.getUTCHours())}:${pad(instant.getUTCMinutes())}Z`;
}

/** Military date-time group '220600ZOCT26' */
export function formatDtg(instant) {
  if (!instant) return '';
  return `${pad(instant.getUTCDate())}${pad(instant.getUTCHours())}${pad(instant.getUTCMinutes())}Z${MONTHS[instant.getUTCMonth()].toUpperCase()}${String(instant.getUTCFullYear()).slice(2)}`;
}

/** 'D+3 (25 Oct 2026 06:00Z)' for a day of the operation, or 'D+3' when no date is set. D+1 is the first day. */
export function dayLabelZulu(startDate, startTimeZ, day) {
  const i = startDate ? instantOfDay(startDate, startTimeZ, day) : null;
  return i ? `D+${day} (${formatZulu(i)})` : `D+${day}`;
}

/** 'Operation period: 22 Oct 2026 06:00Z to 28 Oct 2026 06:00Z (7 days)' or '' when no date is set. */
export function operationPeriodLabel(startDate, startTimeZ, durationDays) {
  if (!startDate) return '';
  return `${formatZulu(startInstant(startDate, startTimeZ))} to ${formatZulu(endInstant(startDate, startTimeZ, durationDays))} (${durationDays} ${durationDays === 1 ? 'day' : 'days'})`;
}
